import { Button, chakra, Spinner, Text, VStack } from "@chakra-ui/react"
import { useScopedTranslation } from "@pathwright/ux/src/components/lng/useScopedTranslation"
import useTranslate from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { Formik } from "formik"
import { toFormikValidate } from "zod-formik-adapter"
import { ActionType } from "../../../../lib/types"
import { useActionState } from "../../../../lib/utils/action-state"
import { useSpaceContext } from "../../../space/api/context"
import AuthFormAlert from "../../components/AuthFormAlert"
import AuthFormButton from "../../components/AuthFormButton"
import AuthFormHeading from "../../components/AuthFormHeading"
import AuthMotion from "../../components/AuthMotion"
import useColorModeBranding from "../../utils/useColorModeBranding"
import PasswordInput from "../components/PasswordInput"
import { schema } from "./api/action"
import PasswordsForm from "./PasswordsForm"

const validate = toFormikValidate(schema)

const PasswordChangeView = ({
  data,
  loading,
  success,
  error,
  onSubmit,
  onContinue,
  onCancel,
  showHeading = true
}: ActionType & {
  onContinue: Function
  onCancel?: Function
  showHeading?: boolean
}) => {
  const colorPalette = useColorModeBranding()
  const { user } = useSpaceContext()
  // NOTE: there should always be a user + email.
  const email = user?.email || ""
  const { t: scopedT } = useScopedTranslation("password_form.fields")
  const { t } = useTranslate()
  const useActiveActionState = useActionState({
    data,
    loading,
    success,
    error,
    onSubmit
  })
  const changePassword = useActiveActionState()
  const sendPasswordResetEmail = useActiveActionState()

  if (sendPasswordResetEmail.data?.success) {
    return (
      <AuthMotion>
        <VStack gap={4}>
          <AuthFormHeading>Check your email</AuthFormHeading>
          <Text>
            We sent a link to {email} with instructions for resetting your
            password.
          </Text>
        </VStack>
      </AuthMotion>
    )
  }

  if (changePassword.data?.success) {
    return (
      <AuthMotion>
        <VStack gap={4}>
          <Pathicon icon="check-circle-fill" color="border" size={30} />
          <AuthFormHeading>Password updated</AuthFormHeading>
          <Text>You can use your new password to sign-in from now on.</Text>
          <Button
            variant="solid"
            colorPalette={colorPalette}
            onClick={() => onContinue()}
            width="full"
          >
            Continue
          </Button>
        </VStack>
      </AuthMotion>
    )
  }

  return (
    <Formik
      initialValues={{
        email,
        currentPassword: "",
        password1: "",
        password2: ""
      }}
      validate={validate}
      onSubmit={() => {}} // noop
      schema={schema}
    >
      {(form) => (
        <AuthMotion>
          <form
            hidden
            id="startPasswordRetrieval"
            onSubmit={sendPasswordResetEmail.onSubmit}
          />
          <chakra.form onSubmit={changePassword.onSubmit} width="100%">
            <VStack gap={4} width="100%">
              <AuthFormAlert error={error} />
              {showHeading && (
                <VStack gap={4} width="100%">
                  <AuthFormHeading>Update Password</AuthFormHeading>
                  <Text textAlign="center">
                    Passwords must be at least ten characters.
                  </Text>
                  <Text>
                    <b>Email:</b> {email}
                  </Text>
                  <input hidden readOnly name="email" value={email} />
                </VStack>
              )}
              <PasswordInput
                id="currentPassword"
                name="currentPassword"
                placeholder={scopedT(`current_ph`) + "..."}
                disabled={loading}
              />
              <input
                hidden
                readOnly
                name="email"
                value={email}
                form="startPasswordRetrieval"
              />
              <Button
                variant="plain"
                colorPalette={colorPalette}
                alignSelf="flex-end"
                textDecor="underline"
                form="startPasswordRetrieval"
                value={email}
                type="submit"
                disabled={loading}
                // Preventing tabbing to the "Forgot password?" button when user is
                // clearly intending to enter new password.
                tabIndex={
                  form.values.currentPassword && !form.touched.currentPassword
                    ? -1
                    : undefined
                }
              >
                {sendPasswordResetEmail.loading && <Spinner size="sm" mr={2} />}
                {t("auth.sign_in_form.forgot_password")}
              </Button>
              <PasswordsForm isSubmitting={loading} />
              <AuthFormButton loading={changePassword.loading}>
                {scopedT("confirm")}
              </AuthFormButton>
              {!!onCancel && (
                <Button variant="solid" onClick={() => onCancel()} width="full">
                  {scopedT("cancel")}
                </Button>
              )}
            </VStack>
          </chakra.form>
        </AuthMotion>
      )}
    </Formik>
  )
}

export default PasswordChangeView
